import axios from 'axios';
import { useEffect, useState } from 'react';
import pageService from '../../services/flows/flows.service';
import UrlsUtil from '../../utils/url.util';

// @ts-ignore
import ReactJson from '@microlink/react-json-view';

type FlowsPersistence = {
  activeFlows: string,
  flowDefault: string,
  persisted: string[],
  rawData: any[],
  lastUpdated: string | undefined,
};

function Flows() {

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [persistence, setPersistence] = useState<FlowsPersistence>({
    activeFlows: '',
    flowDefault: '',
    persisted: [],
    rawData: [],
    lastUpdated: 'loading -- please wait...',
  });

  const getData = async () => {
    if (!isFetching) {
      setPersistenceProperty('lastUpdated', `querying data...`);
      setIsFetching(true);

      let rawData: any = undefined;
      const now = new Date();

      await pageService.getAll()
        .then((response: any) => {

          if (response.status === 200 && response.data) {

            response.data.sort((a: any, b: any) => {
              var aKey = `${a.entityType}_${a.version}`;
              var bKey = `${b.entityType}_${b.version}`;
              if (aKey > bKey) return -1;
              if (aKey < bKey) return 1;
              return 0;
            });

            const uniqueFlows: any[] = [];
            for (let i = 0; i < response.data.length; i++) {
              const flow = response.data[i];
              if (!uniqueFlows.some(x => x.key === flow.key && x.version === flow.version)) {
                uniqueFlows.push(flow);
              }
            }

            rawData = uniqueFlows;
          }
        }).then(async () => {
          await pageService.getActive()
            .then((activeResponse: any) => {
              setPersistence(current => ({
                ...current,
                'lastUpdated': `last update: ${now.getHours()}:${`0${now.getMinutes()}`.slice(-2)}:${`0${now.getSeconds()}`.slice(-2)}`,
                'persisted': activeResponse.data,
                'rawData': rawData
              }));
            })
        })
        .finally(() => setIsFetching(false));
    }
  };

  const [file, setFile] = useState<any>()

  const handleFileChange = (event: any) => {
    setFile(event.target.files[0]);
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()
    const apiBaseUrl = UrlsUtil.determineApiBaseUrl(window);

    const url = `${apiBaseUrl}/api/Flow/upload`;;
    const formData = new FormData();
    formData.append('formFile', file);
    formData.append('fileName', file.name);


    let authToken = window.localStorage.getItem('basicAuthHeader')
    let headers = { headers: { 'Authorization': authToken, 'content-type': 'multipart/form-data', } }

    let response = await axios.post(url, file, headers);
    console.log(response);
    await getData();
  }

  const handleChange = (e: any, name: string, value: string) => {
    const formattedValue = `${value},`;
    let current = e.target;
    // @ts-ignore
    let newValue = persistence[name];
    if (current.checked) {
      newValue += formattedValue;
    } else {
      newValue = newValue.replace(formattedValue, '');
    }
    setPersistence(v => { return { ...v, [name]: newValue } });
  };

  const persistActive = async () => {
    await pageService.persist(persistence.activeFlows)
      .then(() => resetInteractiveElements())
      .then(async () => await getData());
  };

  const persistDefault = async () => {
    const promisesToKeep: Promise<any>[] = [];
    persistence.flowDefault.split(',').forEach((partSet: string) => {
      var parts = partSet.split('_');
      if (parts.length === 2)
        promisesToKeep.push(pageService.persistDefault(parts[0], parts[1]));
    });
    await Promise.all(promisesToKeep)
      .then(() => resetInteractiveElements())
      .then(async () => await getData());
  };

  const persistInactive = async () => {
    await pageService.persistInactive(persistence.activeFlows)
      .then(() => resetInteractiveElements())
      .then(async () => await getData());
  };


  const populate = async () => {
    await pageService.populate()
      .then(async () => await getData());
  };

  const resetInteractiveElements = async () => {
    const elementsToUpdate = document.querySelectorAll('.activeFlows');
    for (let etu = 0; etu < elementsToUpdate.length; etu++) {
      // @ts-ignore
      elementsToUpdate[etu].checked = false;
    }

    setPersistence(current => ({
      ...current,
      'activeFlows': '',
      'flowDefault': ''
    }));
  };

  const setPersistenceProperty = (name: string, value: any) => {
    setPersistence(current => {
      const updated = { ...current, [name]: value };
      return updated;
    });
  };

  useEffect(() => {
    const tokenCheck = localStorage.getItem('token');
    if (!tokenCheck) {
      window.location.href = "/";
    }

    if (!isInitialized) {
      setIsInitialized(true);

      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, persistence]);

  return (
    <div className='data-display'>
      <div className="row" style={{ width: '100%', textAlign: 'left', marginBottom: '0.5em' }}>
        <div className='col-12'>
          <h1>Flows Manager</h1>
          <div>
            {persistence.rawData?.length || 0} defined flow{persistence.rawData?.length !== 1 || false ? 's' : ''} &bull; <span style={{ marginTop: '0em' }}>{persistence.lastUpdated}</span>

            {
              persistence.rawData?.length > 0 &&
              <>
                <button className='btn btn-primary header-refresh header-button-spaced' onClick={() => persistActive()} disabled={persistence.activeFlows.length <= 0}>Set checked as Active</button>
                <button className='btn btn-danger header-refresh header-button-spaced' onClick={() => persistInactive()} disabled={persistence.activeFlows.length <= 0}>Set checked as Inactive</button>
                <button className='btn btn-secondary header-refresh header-button-spaced' onClick={() => persistDefault()} disabled={persistence.flowDefault.length <= 3}>Set checked as Default</button>
              </>
            }

            <button className='btn btn-success header-refresh header-button-spaced' onClick={() => populate()}>Force Cache Refresh</button>
          </div>
        </div>
      </div>
      <div>
        <h3>Upload new flow</h3>
        <form onSubmit={handleSubmit}>
          <h1>React File Upload</h1>
          <input type="file" onChange={handleFileChange} />
          <button type="submit">Upload</button>
        </form>
      </div>
      <table className='table table-striped table-hover'>
        <tbody>
          {
            persistence.rawData.length === 0
              ? <tr><td><strong>No matching records to display</strong></td></tr>
              : persistence.rawData.map((flow: any, i) => {

                // const cacheKey = `${flow.entityType}_${flow.version}`;

                // console.log();
                // console.log('cacheKey', cacheKey);
                // console.log('isActive', flow.isActive);
                // console.log('isDefault', flow.isDefault);

                return <tr key={i}>
                  <td>
                    <div className='row'>
                      <div className='col-12'>
                        <h2>
                          <input type="checkbox" name='activeFlows' className='activeFlows' onChange={(e) => { handleChange(e, 'activeFlows', `${flow.entityType}_${flow.version}`); handleChange(e, 'flowDefault', `${flow.key}_${flow.version}`); }} /> {flow.name}
                          <strong style={{ color: 'green', marginLeft: '0.5em' }} dangerouslySetInnerHTML={{ __html: flow.isActive ? 'ACTIVE' : '' }}></strong>
                          <strong style={{ color: 'black', marginLeft: '0.5em' }} dangerouslySetInnerHTML={{ __html: flow.isActive && flow.isDefault ? '-' : '' }}></strong>
                          <strong style={{ color: 'blue', marginLeft: '0.5em' }} dangerouslySetInnerHTML={{ __html: flow.isDefault ? `DEFAULT ${flow.entityType.toString().toUpperCase()} FLOW` : '' }}></strong>
                        </h2>
                        <strong>Description:</strong> {flow.description}
                        <div className='row'>
                          <div className='col-4 row-left-panel'>
                            <p>
                              <strong>Version:</strong> {flow.version}<br />
                              <strong>Entity Type:</strong> {flow.entityType}<br />
                              <strong>Flow Type:</strong> {flow.flowType}<br />
                              <strong>Is Default:</strong> {flow.isDefault ? 'Yes' : 'No'}
                            </p>
                            <div>
                              <strong>Configured States:</strong><br />
                              {
                                flow.states && <ul style={{ paddingLeft: 0, listStyleType: 'none' }}>
                                  {
                                    flow.states.map((fs: any, si: number) => {
                                      return <li key={`${si}${fs.key}`} style={{ marginLeft: 0, paddingLeft: 0 }}><pre style={{ paddingBottom: 0, marginBottom: 0 }}>{(si < 10 ? ' ' : '')}{si}:&nbsp;{fs.key}</pre></li>
                                    })
                                  }

                                </ul>
                              }
                            </div>
                          </div>
                          <div className='col-8 row-right-panel' style={{ height: 600, overflowY: 'scroll' }}>
                            <div>Raw Data:</div><br />
                            <div> <ReactJson src={flow} collapsed={2} displayDataTypes={false} name={false} /> </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              })
          }
        </tbody>
      </table>
    </div>
  );
};

export default Flows;